import { getTraceId } from './tracing';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
type Data = Record<string, unknown>;

interface HttpResponse<T> extends Response {
  parsedBody?: T;
}

async function http<T>(
  method: HttpMethod,
  url: string,
  data?: Data,
): Promise<HttpResponse<T>> {
  const options = {
    method,
    redirect: 'manual' as RequestRedirect,
    headers: {
      'Content-Type': 'application/json',
      'X-Trace-Id': getTraceId(),
    } as Record<string, string>,
    body: data ? JSON.stringify(data) : undefined,
    credentials: 'same-origin' as RequestCredentials,
  };

  if ('PC_AUTH_TOKEN' in window) {
    options.headers.Authorization = `Bearer ${PC_AUTH_TOKEN}`;
    options.credentials = 'omit' as RequestCredentials;
  }

  if ('PC_USER_AGENT' in window) {
    options.headers['User-Agent'] = PC_USER_AGENT;
  }

  const response: HttpResponse<T> = await fetch(url, options);
  const { status } = response;

  if (status !== 0 && (status < 204 || status > 206) && status !== 500) {
    try {
      response.parsedBody = await response.json();
    } catch (e) {
      console.error(e); // eslint-disable-line
    }
  }

  return response;
}

async function get<T>(url: string, data?: Data): Promise<HttpResponse<T>> {
  return http('GET', url, data);
}

async function del<T>(url: string, data?: Data): Promise<HttpResponse<T>> {
  return http('DELETE', url, data);
}

async function post<T>(url: string, data?: Data): Promise<HttpResponse<T>> {
  return http('POST', url, data);
}

async function put<T>(url: string, data?: Data): Promise<HttpResponse<T>> {
  return http('PUT', url, data);
}

async function patch<T>(url: string, data?: Data): Promise<HttpResponse<T>> {
  return http('PATCH', url, data);
}

export { get, del, post, put, patch };
